import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import DataTable from "./../../../Layout/Table/TableLayout";
import Navbar from "../../../Shared/Sales-ExecutiveNavbar/Navbar";
import "./Customer.css";
import { FaEye, FaEdit, FaCopy } from "react-icons/fa";
import { baseURL } from "../../../Apiservices/Api";
import { AuthContext } from '../../../AuthContext/AuthContext';
import { useNavigate,useLocation } from 'react-router-dom';
import Loader from "../../../Loader/Loader";

const SalesCustomer = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [message, setMessage] = useState("");
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("");
  const { authToken, userId } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [pageIndex, setPageIndex] = useState(0);
  const queryClient = useQueryClient();

  // ── Fetch tags (unchanged) ────────────────────────────────────────────────
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/tags`);
        setTags(response.data);
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };
    fetchTags();
  }, []);

  // ── Restore page from URL (unchanged) ────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageFromUrl = parseInt(params.get("page"));
    if (!isNaN(pageFromUrl)) {
      setPageIndex(pageFromUrl - 1);
    }
  }, [location.search]);

  // ── React Query: fetch matched customers ──────────────────────────────────
  const { data = [], isLoading, isError } = useQuery({
    queryKey: ["salesCustomers", userId, selectedTag],
    refetchInterval: 3000,
    queryFn: async () => {
      if (!userId) return [];

      // If a tag is selected, use tag filter endpoint
      if (selectedTag) {
        const response = await axios.get(`${baseURL}/api/tagfilter`, {
          params: { tag: selectedTag },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        return response.data;
      }

      // Otherwise fetch leads + customers and match them
      const [leadsRes, customersRes] = await Promise.all([
        axios.get(`${baseURL}/api/allleads`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
        axios.get(`${baseURL}/api/customers`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
      ]);

      const filteredLeads = leadsRes.data.filter(
        (lead) => lead.assignedSalesId == userId && lead.status === "opportunity"
      );

      return customersRes.data
        .filter((customer) =>
          filteredLeads.some((lead) => lead.customerid == customer.id)
        )
        .map((customer) => ({
          ...customer,
          formattedId: `CUS${String(customer.id).padStart(4, "0")}`,
        }));
    },
    enabled: !!userId,
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage("Copied to clipboard!");
      setTimeout(() => setMessage(""), 1000);
    }).catch((err) => console.error("Failed to copy:", err));
  };

  const navigateToLead = (customerId, currentPage = pageIndex) => {   
         const params = new URLSearchParams(location.search);
  const pageSize = params.get('pageSize') || '20';


    navigate(`/customerdetails/${customerId}?page=${currentPage + 1}&pageSize=${pageSize}`);
  };

  const navigateToEditLead = (customerId, currentPage = pageIndex) => {
    const params = new URLSearchParams(location.search);
    const pageSize = params.get('pageSize') || '20';
    navigate(`/editcustomerdetails/${customerId}?page=${currentPage + 1}&pageSize=${pageSize}`);
  };

  // ── Columns (unchanged) ───────────────────────────────────────────────────
  const columns = React.useMemo(
    () => [
      {
        Header: "Customer ID",
        accessor: "id",
      },
      {
        Header: "Name",
        accessor: "name",
          Cell: ({ row }) => {
        const currentPage = pageIndex; 
        const params = new URLSearchParams(location.search);
        const pageSize = params.get('pageSize') || '20';
        return (
          <div
            style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
            onClick={() => navigateToLead(row.original.id,currentPage,pageSize)}
          >
            {row.original.name}
          </div>
        )
      }
      },
      {
        Header: "Mobile",
        accessor: "phone_number",
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <a
              href={`https://wa.me/${row.original.phone_number}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", color: "blue", cursor: "pointer" }}
              title="Chat on WhatsApp"
            >
              {row.original.phone_number}
            </a>
            <FaCopy
              style={{ marginLeft: "8px", cursor: "pointer", color: "#ff9966" }}
              onClick={() => copyToClipboard(row.original.phone_number)}
              title="Copy Phone Number"
            />
          </div>
        ),
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", maxWidth: "200px" }}>
            <div
              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "150px" }}
              title={row.original.email}
            >
              {row.original.email}
            </div>
            <FaCopy
              style={{ cursor: "pointer", color: "#ff9966" }}
              onClick={() => copyToClipboard(row.original.email)}
              title="Copy Email"
            />
          </div>
        ),
      },
      {
        Header: "Origin City",
        accessor: "origincity",
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const currentPage = pageIndex;
                    const params = new URLSearchParams(location.search);
    const pageSize = params.get('pageSize') || '20';


          return (
            <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
              <FaEye
                style={{ color: "#ff9966", cursor: "pointer" }}
                onClick={() => navigateToLead(row.original.id, currentPage,pageSize)}
              />
              <FaEdit
                style={{ color: "#ff9966", cursor: "pointer" }}
                onClick={() => navigateToEditLead(row.original.id, currentPage,pageSize)}
              />
            </div>
          );
        },
      },
    ],
    [pageIndex,location]
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="SaleCustomercontainer">
      <Navbar onToggleSidebar={setCollapsed} />
      <div className={`SaleCustomer ${collapsed ? "collapsed" : ""}`}>
        <div className="SaleCustomer-container mb-5">
          <div className="SaleCustomer-table-container">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3>Customer Details</h3>
              <select
                className="form-select w-25"
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
              >
                <option value="">All Tags</option>
                {tags.map((tag) => (
                  <option key={tag.id} value={tag.value}>
                    {tag.value}
                  </option>
                ))}
              </select>
            </div>

            {message && <div className="alert alert-success">{message}</div>}

            {isLoading ? (
                                             <Loader />
                                           ) : data.length === 0 ? (
                                                       <div className="text-center p-5">
                                                         <p>No data available</p>
                                                       </div>
                                                     ) : (
              <DataTable
                columns={columns}
                data={data}
                pageIndex={pageIndex}
                setPageIndex={setPageIndex}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesCustomer;